import React from 'react';
import classNames from 'classnames';
import Immutable from 'immutable';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Paragraph, Block, CoreLink } from '@uncinc/react-drupal-core';
import { Container, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';
import { ImmutableEntity, ImmutableRootState } from '@uncinc/react-drupal-core/types';

import {
  BLOCK_RELATED_THEMES_BLOCK_ID,
  BLOCK_RELATED_DOCUMENTS_BLOCK_ID,
  TAXONOMY_MEDIA_CATEGORY_PUBLICATION,
  TAXONOMY_MEDIA_CATEGORY_CODE,
  BLOCK_RELATED_THEME_ARTICLES_BLOCK_ID,
} from '../../../../config/constants';
import { getOverviewUrlFromMenu } from '../../../../helpers';
import { TaxonomyThemes } from '../../../../types/uncinc_profile/taxonomy';
import VariableRatiosPicture from '../../../../components/VariableRatiosPicture';

import './style.scss';

interface TaxonomyThemePageProps extends React.ComponentPropsWithoutRef<'div'> {
  entity: ImmutableEntity<TaxonomyThemes>;
}

const TaxonomyThemePage: React.FC<TaxonomyThemePageProps> = ({ entity }) => {
  const { t } = useTranslation();
  const classes = classNames('TaxonomyThemePage');
  const theme = entity.toJS();
  const id = theme.tid[0].value;
  const name = theme.name[0].value;
  const description = theme.description[0].value;
  const paragraphs = theme.field_paragraphs;
  const parentId = theme.parent[0]?.target_id;
  const themeMedia = theme.field_media_item[0];

  const lastBreadcrumbTitle = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['entity', 'breadcrumbs', 1, 'title']));
  const lastBreadcrumbUrl = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['entity', 'breadcrumbs', 1, 'url']));

  const menu = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['menu', 'main']));
  const overviewUrl = getOverviewUrlFromMenu(menu, 'themes');
  const documentsOverviewUrl = `${getOverviewUrlFromMenu(menu, 'publications')}?field_themes_target_id=${id}`;
  const articlesOverviewUrl = `${getOverviewUrlFromMenu(menu, 'articles')}?field_themes_target_id=${id}`;

  const blockFilters = {
    tid_raw: id,
  };

  const articleBlockFilters = {
    field_themes_target_id: [id],
  };

  const documentBlockFilters = {
    field_media_category_target_id: [
      TAXONOMY_MEDIA_CATEGORY_PUBLICATION,
      TAXONOMY_MEDIA_CATEGORY_CODE,
    ],
    field_themes_target_id: [id],
  };

  return (
    <article itemType="http://schema.org/WebPage" className={classes}>
      <Container>

        <div className="TaxonomyThemePage__hero__image">
          {themeMedia && (
            <VariableRatiosPicture
              image={themeMedia}
              // @ts-ignore 4_1 is not in our own ratios
              aspectRatios={['3_1', '3_1', '4_1', '4_1', '4_1']}
            />
          )}
        </div>

        <div className="row">
          <div className="col-12">
            <ol className="Breadcrumbs" vocab="https://schema.org/" typeof="BreadcrumbList">
              <li className="Breadcrumbs__item" property="itemListElement" typeof="ListItem">
                <CoreLink href="/">Home</CoreLink>
              </li>
              <li className="Breadcrumbs__item" property="itemListElement" typeof="ListItem">
                <CoreLink href={overviewUrl}>Onze thema&#39;s</CoreLink>
              </li>
              {lastBreadcrumbTitle && lastBreadcrumbUrl && (
                <li className="Breadcrumbs__item" property="itemListElement" typeof="ListItem">
                  <CoreLink href={lastBreadcrumbUrl}>{lastBreadcrumbTitle}</CoreLink>
                </li>
              )}
              <li className="Breadcrumbs__item" property="itemListElement" typeof="ListItem">
                <span>{name}</span>
              </li>
            </ol>
          </div>
          <div className="col-12 col-lg-8">
            <div className="TaxonomyThemePage__title">
              <h1>{name}</h1>
            </div>

            <div className="TaxonomyThemePage__body">
              <WysiwygField
                html={description}
              />
            </div>
          </div>
        </div>
      </Container>

      {/* Related articles */}
      <Block
        id={BLOCK_RELATED_THEME_ARTICLES_BLOCK_ID}
        link={articlesOverviewUrl}
        linkTitle={t('content_types.article.more')}
        viewId="article"
        viewDisplay="related"
        viewFilters={articleBlockFilters}
      />

      <div className="TaxonomyThemePage__paragraphs Spacing--bottom">
        {paragraphs.map((paragraph) => {
          const paragraphId = paragraph.target_id;
          const language = paragraph.entity.langcode[0].value;
          return (
            <Paragraph
              key={`paragraph:${paragraphId}:${language}`}
              entity={Immutable.fromJS(paragraph.entity)}
            />
          );
        })}
      </div>

      {/* Related documents */}
      <Block
        id={BLOCK_RELATED_DOCUMENTS_BLOCK_ID}
        link={documentsOverviewUrl}
        linkTitle={t('content_types.document.overview')}
        viewId="document"
        viewDisplay="documents_highlighted"
        viewFilters={documentBlockFilters}
      />

      {/* Related themes */}
      <Block
        id={BLOCK_RELATED_THEMES_BLOCK_ID}
        link={overviewUrl}
        linkTitle={t('taxonomy_types.themes.overview')}
        viewId="theme"
        viewDisplay="related"
        viewFilters={blockFilters}
        viewArgs={[parentId]}
      />
    </article>
  );
};

export default TaxonomyThemePage;
